import React, {
  createContext,
  useContext,
  useMemo,
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useDocumentationContext } from '../../context/DocumentationContext';
import { NodeEntityTypes } from '../../../sweep-canvas/canvas-types';
import { handleOnPillClick } from '../../../../reducers/documentation/documentationReducer';
import { useDispatch, useSelector } from 'react-redux';
import {
  DocumentationTabTypes,
  SWEEP_ELEMENTS_TAB_TYPES,
} from '../../../../types/enums/DocumentationTabTypes';
import SweepStagesModel from '../../../../models/stagesModel';
import { getDocumentationTabFromCanvasPill } from '../../getDocumentationTabFromCanvasPill';
import { selectParsedRecordTypes } from '../../../../reducers/global/parserReducer';
import { selectDefaultCreationCrmOrgId } from '../../../../reducers/userInfoReducer';
import { DocumentationFiltersTypesMap, RecordTypeFilterItem } from './filterTypes';
import { telemetry } from '../../../../telemetry';
import { ActiveState } from '../../building-blocks/activity-state/helpers';
import { EntityClicked } from '../../context/types';
import isEqual from 'lodash/isEqual';
import {
  setActivePageTab,
  DEFAULT_OBJECTS_TAB_ID,
} from '../../../../reducers/documentation/documentationPageReducer';
import { DocumentationPageType } from '../../../../components/documentation/types';
import { setDocumentationPageType } from '../../../../reducers/documentation/documentationPageReducer';
import { createRecordTypeIdentifier, createStepId } from '../../pills/pillsUtils';

interface BaseFilterValues {
  [filterKey: string]: {
    selectedValues: string[];
    isAllSelected: boolean;
    stepsForSelectedRecordTypes?: string[];
  };
}

const baseFilterValues: BaseFilterValues = {
  [DocumentationFiltersTypesMap.activeState]: {
    selectedValues: [ActiveState.activeOnly],
    isAllSelected: false,
  },
};

interface DocumentationFiltersContextValue {
  filterValues: BaseFilterValues;
  setFilterValue: (filterKey: string, selectedValues: string[]) => void;
  clearAllFilterValues: () => void;
  setRecordTypesItems: (items: RecordTypeFilterItem[]) => void;
}

interface DocumentationFiltersProviderProps {
  children: ReactNode;
}

const DocumentationFiltersContext = createContext<DocumentationFiltersContextValue | undefined>(
  undefined,
);

/**
 * Provider that manages documentation filter values state only
 * Filter definitions are now passed via props to components
 */
export const DocumentationFiltersProvider: React.FC<DocumentationFiltersProviderProps> = ({
  children,
}) => {
  const { entityClicked, setEntityClicked } = useDocumentationContext();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const parsedRecordTypes = useSelector(selectParsedRecordTypes(crmOrgId ?? ''));
  const dispatch = useDispatch();

  // Filter values state management only
  const [filterValues, setFilterValues] = useState<BaseFilterValues>(baseFilterValues);
  const [initialEntityClicked, setInitialEntityClicked] = useState<EntityClicked | null>(null);
  const recordTypesItemsRef = useRef<RecordTypeFilterItem[]>([]);

  const getStepsForRecordTypes = useCallback((selectedRecordTypes: string[]) => {
    if (!selectedRecordTypes.length) return undefined;

    // If ref is empty, return undefined to indicate "data not ready yet"
    // vs returning [] which means "no steps for this record type"
    if (!recordTypesItemsRef.current.length) return undefined;

    return recordTypesItemsRef.current
      .filter((rt) => selectedRecordTypes.includes(rt.value))
      .flatMap((rt) => rt.steps.map((step) => step.value));
  }, []);

  const setRecordTypesItems = useCallback(
    (items: RecordTypeFilterItem[]) => {
      recordTypesItemsRef.current = items;

      // If there's an active record type filter, re-compute stepsForSelectedRecordTypes
      // now that the ref is populated. This handles the race condition where the filter
      // was set before the ref was ready.
      setFilterValues((prevFilters) => {
        const recordTypesFilter = prevFilters[DocumentationFiltersTypesMap.recordTypes];
        if (recordTypesFilter?.selectedValues?.length) {
          const stepsForSelectedRecordTypes = getStepsForRecordTypes(
            recordTypesFilter.selectedValues,
          );
          return {
            ...prevFilters,
            [DocumentationFiltersTypesMap.recordTypes]: {
              ...recordTypesFilter,
              stepsForSelectedRecordTypes,
            },
          };
        }
        return prevFilters;
      });
    },
    [getStepsForRecordTypes],
  );

  const setFilterValue = useCallback(
    (filterKey: string, selectedValues: string[]) => {
      const isRecordTypesChange = filterKey === DocumentationFiltersTypesMap.recordTypes;

      const clearStepFilterIfRecordTypesChanged: BaseFilterValues = isRecordTypesChange
        ? {
            [DocumentationFiltersTypesMap.steps]: {
              selectedValues: [],
              isAllSelected: true,
            },
          }
        : {};

      const stepsForSelectedRecordTypes = isRecordTypesChange
        ? getStepsForRecordTypes(selectedValues)
        : undefined;

      setFilterValues((prev) => ({
        ...prev,
        ...clearStepFilterIfRecordTypesChanged,
        [filterKey]: {
          selectedValues,
          isAllSelected: selectedValues.length === 0,
          ...(stepsForSelectedRecordTypes !== undefined && { stepsForSelectedRecordTypes }),
        },
      }));
    },
    [getStepsForRecordTypes],
  );

  const replaceFilterValue = useCallback(
    (newFilters: { filterKey: string; selectedValues: string[] }[]) => {
      setFilterValues(() => {
        const updatedFilters: BaseFilterValues = { ...baseFilterValues };

        newFilters.forEach(({ filterKey, selectedValues }) => {
          const isRecordTypesChange = filterKey === DocumentationFiltersTypesMap.recordTypes;
          const stepsForSelectedRecordTypes = isRecordTypesChange
            ? getStepsForRecordTypes(selectedValues)
            : undefined;

          updatedFilters[filterKey] = {
            selectedValues,
            isAllSelected: selectedValues.length === 0,
            ...(stepsForSelectedRecordTypes !== undefined && { stepsForSelectedRecordTypes }),
          };
        });

        return updatedFilters;
      });
    },
    [getStepsForRecordTypes],
  );

  const clearAllFilterValues = useCallback(() => {
    setFilterValues(baseFilterValues);
  }, []);

  const value: DocumentationFiltersContextValue = useMemo(
    () => ({
      filterValues,
      setFilterValue,
      clearAllFilterValues,
      setRecordTypesItems,
    }),
    [filterValues, setFilterValue, clearAllFilterValues, setRecordTypesItems],
  );

  useEffect(() => {
    if (entityClicked && !isEqual(entityClicked, initialEntityClicked)) {
      setInitialEntityClicked({ ...entityClicked }); //force new object reference
      setEntityClicked(null);
    }
  }, [entityClicked, initialEntityClicked, setEntityClicked]);

  //handle canvas pill click
  useEffect(() => {
    if (initialEntityClicked) {
      const { funnelId, recordTypeApiName, entity, funnelsData, recordTypesData, stepId } =
        initialEntityClicked;
      const funnel = funnelId ? funnelsData?.[funnelId] : undefined;
      const recordType = recordTypeApiName ? recordTypesData?.[recordTypeApiName] : undefined;

      if (!funnel && !recordType) {
        telemetry.captureError(
          'DocumentationFiltersContext: No funnel or recordType found',
          initialEntityClicked,
        );
        return;
      }

      dispatch(
        setDocumentationPageType({
          documentationPageType: DocumentationPageType.metadataTypes,
        }),
      );

      if (entity.type === NodeEntityTypes.GroupLabel) {
        const objectName = funnel
          ? funnel.funnelDetails.leadingObject.objectName
          : recordType?.objectName;
        const rtName = recordType?.name ?? funnel?.recordType.name;

        if (objectName) {
          dispatch(
            handleOnPillClick({
              singleObjectName: objectName,
              tab: DocumentationTabTypes.RECORD_TYPES,
            }),
          );
          dispatch(
            setActivePageTab({
              tabId: DEFAULT_OBJECTS_TAB_ID,
            }),
          );

          const funnelId = funnel?.id ?? '';
          replaceFilterValue([
            {
              filterKey: DocumentationFiltersTypesMap.recordTypes,
              selectedValues: [
                createRecordTypeIdentifier({
                  recordTypeName: rtName ?? '',
                  objectName,
                  funnelId,
                }),
              ],
            },
          ]);
        }
        return;
      }

      // Support only pill clicks
      if (entity.type !== NodeEntityTypes.StepPill && entity.type !== NodeEntityTypes.GroupPill) {
        return;
      }

      let selectedRecordTypeValue: string | undefined;
      let stage: SweepStage | undefined;
      let selectedStepName: string | undefined;
      let singleObjectApiName = '';

      const defaultActiveTab = getDocumentationTabFromCanvasPill(entity.canvasPillType);
      const isSweepElement = SWEEP_ELEMENTS_TAB_TYPES.includes(
        defaultActiveTab as DocumentationTabTypes,
      );

      if (funnel) {
        selectedRecordTypeValue = createRecordTypeIdentifier({
          recordTypeName: funnel.recordType.name,
          objectName: funnel.recordType.objectName,
          funnelId: funnel.id,
        });

        const stagesModel = new SweepStagesModel(funnel.funnelDetails?.stages || []);
        stage = stepId ? stagesModel.stageByIdOrUndefined(stepId)?.getStage() : undefined;

        selectedStepName =
          isSweepElement && stage
            ? stepId
            : stage
              ? createStepId({
                  objectName: funnel.recordType.objectName,
                  stepFullName: stage.stageName,
                  leadingField: funnel.funnelDetails.leadingObject.fieldName,
                })
              : undefined;
        singleObjectApiName = funnel.recordType.objectName ?? '';
      }

      if (recordType && !funnel) {
        selectedRecordTypeValue = createRecordTypeIdentifier({
          recordTypeName: recordType.name,
          objectName: recordType.objectName,
          funnelId: '',
        });
        selectedStepName = stepId;
        singleObjectApiName = recordType.objectName ?? '';
      }

      if (defaultActiveTab) {
        dispatch(
          handleOnPillClick({
            singleObjectName: singleObjectApiName,
            tab: defaultActiveTab,
          }),
        );

        const newFilters: { filterKey: string; selectedValues: string[] }[] = [];
        if (selectedRecordTypeValue) {
          newFilters.push({
            filterKey: DocumentationFiltersTypesMap.recordTypes,
            selectedValues: [selectedRecordTypeValue],
          });
        }

        if (selectedStepName) {
          newFilters.push({
            filterKey: DocumentationFiltersTypesMap.steps,
            selectedValues: [selectedStepName],
          });
        }

        dispatch(
          setActivePageTab({
            tabId: DEFAULT_OBJECTS_TAB_ID,
          }),
        );
        replaceFilterValue(newFilters);
      }
    }
  }, [initialEntityClicked, dispatch, replaceFilterValue, parsedRecordTypes]);

  return (
    <DocumentationFiltersContext.Provider value={value}>
      {children}
    </DocumentationFiltersContext.Provider>
  );
};

/**
 * Hook to access DocumentationFilters context (only for filter values state)
 */
export const useDocumentationFiltersContext = () => {
  const context = useContext(DocumentationFiltersContext);
  if (context === undefined) {
    throw new Error(
      'useDocumentationFiltersContext must be used within a DocumentationFiltersProvider',
    );
  }
  return context;
};
